import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initAnalytics, trackEvent, trackPageView } from '../../features/analytics';

const CLICKABLE_SELECTOR = 'a, button, [role="button"], input[type="button"], input[type="submit"]';

function getElementLabel(element: Element) {
  const ariaLabel = element.getAttribute('aria-label')?.trim();
  const title = element.getAttribute('title')?.trim();
  const text = element.textContent?.replace(/\s+/g, ' ').trim();

  return ariaLabel || title || text || element.getAttribute('name') || element.id || element.tagName.toLowerCase();
}

function getSafeHref(element: Element) {
  if (!(element instanceof HTMLAnchorElement) || !element.href) {
    return null;
  }

  try {
    const url = new URL(element.href);
    return url.origin === window.location.origin
      ? url.pathname
      : url.origin;
  } catch {
    return null;
  }
}

export default function BehaviorAnalytics() {
  const location = useLocation();
  const lastTrackedPathRef = useRef('');

  useLayoutEffect(() => {
    // Router state retains the one-time link while external scripts see a clean URL.
    if (/^\/(reset-password|verify-email)$/.test(location.pathname) && (location.search || location.hash)) {
      window.history.replaceState(window.history.state, '', location.pathname);
    }
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    initAnalytics();
  }, [location.pathname]);

  useEffect(() => {
    const path = location.pathname;
    if (lastTrackedPathRef.current === path) {
      return;
    }

    lastTrackedPathRef.current = path;
    trackPageView(path);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const target = event.target.closest(CLICKABLE_SELECTOR);
      if (!target || target.closest('[data-analytics-ignore="true"]')) {
        return;
      }

      trackEvent('ui_click', {
        element: target.tagName.toLowerCase(),
        label: getElementLabel(target),
        href: getSafeHref(target),
        analyticsEvent: target.getAttribute('data-analytics-event'),
      });
    };

    const handleSubmit = (event: SubmitEvent) => {
      if (!(event.target instanceof HTMLFormElement) || event.target.closest('[data-analytics-ignore="true"]')) {
        return;
      }

      trackEvent('form_submit', {
        formId: event.target.id || null,
        formName: event.target.getAttribute('name'),
        action: event.target.getAttribute('action'),
      });
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
    };
  }, []);

  return null;
}
